import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, catchError, map, of, tap } from 'rxjs';
import { ApiService } from './api.service';

export interface LoginPayload {
    user_name?: string;
    mobile?: string;
    email?: string;
    pass_word: string;
}

export interface LoginResponse {
    status?: boolean;
    success?: boolean;
    message: string;
    data?: {
        user_id: string;
        name: string;
        email: string;
        mobile: string;
        role: string;
        [key: string]: any;
    };
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly USER_KEY        = 'milko_user';
    private readonly LEDGER_CFG_KEY  = 'milko_ledger_cfg';
    private readonly LOGIN_RAW_KEY   = 'milko_login_raw';
    private readonly TOKEN_KEY       = 'milko_jwt';
    private readonly DAIRY_KEY       = 'milko_selected_dairy_id';
    private readonly userRefreshedSubject = new Subject<any>();
    readonly userRefreshed$ = this.userRefreshedSubject.asObservable();

    constructor(private api: ApiService, private router: Router) { }

    // ── Sign-up helpers ────────────────────────────────────────────────────────

    /**
     * Checks whether a mobile number is already registered.
     * The PHP endpoint returns { success: true } when the mobile IS found
     * (i.e. already registered) and { success: false } when it is NOT found.
     */
    checkMobileExists(mobile: string): Observable<any> {
        return this.api.post<any>('users/validate_mobile_username_email.php', {
            type_check: 'mobile',
            mobile
        });
    }

    /** Sends a SIGNUP OTP to the given Indian mobile number. */
    sendOtp(mobile: string): Observable<any> {
        return this.api.post<any>('sms_service/send_sms.php', {
            user_identifier: mobile,
            purpose: 'SIGNUP'
        });
    }

    /** Verifies the SIGNUP OTP entered by the user. */
    verifyOtp(mobile: string, otp: string): Observable<any> {
        return this.api.post<any>('sms_service/verify_otp.php', {
            user_identifier: mobile,
            otp,
            purpose: 'SIGNUP'
        });
    }

    /** Creates a new user account. */
    register(payload: any): Observable<any> {
        return this.api.post<any>('users/create_milk_users.php', payload);
    }

    // ── Forgot Password helpers ────────────────────────────────────────────────

    /** Sends a FORGOT_PASSWORD OTP to the given mobile number. */
    sendForgotPasswordOtp(mobile: string): Observable<any> {
        return this.api.post<any>('sms_service/send_sms.php', {
            user_identifier: mobile,
            purpose: 'FORGOT_PASSWORD'
        });
    }

    /** Verifies the FORGOT_PASSWORD OTP entered by the user. */
    verifyForgotOtp(mobile: string, otp: string): Observable<any> {
        return this.api.post<any>('sms_service/verify_otp.php', {
            user_identifier: mobile,
            otp,
            purpose: 'FORGOT_PASSWORD'
        });
    }

    /** Resets the user's password via OTP-verified flow. */
    resetPassword(mobile: string, new_password: string): Observable<any> {
        return this.api.post<any>('users/reset_my_password.php', { mobile, new_password });
    }

    // ───────────────────────────────────────────────────────────────────────────

    login(payload: LoginPayload): Observable<LoginResponse> {
        return this.api.post<LoginResponse>('users/app_login.php', payload).pipe(
            tap((res: any) => {
                if ((res?.success || res?.status) && (res?.user || res?.data)) {
                    // Strip heavy arrays from user object before storing in localStorage
                    const userData = res.user || res.data;
                    const trimmedUser = this.stripHeavyFields(userData);
                    this.safeSetItem(this.USER_KEY, JSON.stringify(trimmedUser));
                    this.ensureSelectedDairy(trimmedUser);

                    // Store JWT token
                    if (res?.token) {
                        localStorage.setItem(this.TOKEN_KEY, res.token);
                    }

                    // Save a trimmed raw response — only scalar / config fields, no big arrays
                    const trimmedRaw = this.stripHeavyFields(res);
                    this.safeSetItem(this.LOGIN_RAW_KEY, JSON.stringify(trimmedRaw));

                    // Directly save ledger config — key is 'ledgerConfigs' in this API
                    const cfg = res?.ledgerConfigs ?? res?.ledgerConfig ?? res?.ledger_config
                              ?? res?.system_ledgers ?? res?.voucher_ledgers ?? res?.ledgers ?? null;
                    if (cfg) {
                        this.safeSetItem(this.LEDGER_CFG_KEY, JSON.stringify(cfg));
                    }
                }
            })
        );
    }

    /**
     * Removes known large-array fields from a login response/user object so
     * that localStorage quota is not exceeded on dairies with many members.
     * The stripped fields (dairy accounts, members, rate lists, etc.) are managed
     * by the offline cache service, not persisted here.
     */
    private stripHeavyFields(obj: any): any {
        if (!obj || typeof obj !== 'object') return obj;
        const HEAVY_KEYS = [
            'members', 'all_members', 'member_list',
            'accounts', 'dairy_accounts', 'ac_list',
            'rate_list', 'rate_lists', 'milk_rates',
            'routes', 'route_list',
            'tankers', 'tanker_list',
            'products', 'product_list',
            'transactions', 'ledger_entries',
        ];
        const stripped = { ...obj };
        for (const key of HEAVY_KEYS) {
            if (Array.isArray(stripped[key]) && stripped[key].length > 50) {
                delete stripped[key];
            }
        }
        // Also strip deeply nested heavy arrays inside dairies[]
        if (Array.isArray(stripped.dairies)) {
            stripped.dairies = stripped.dairies.map((d: any) => {
                const sd = { ...d };
                for (const key of HEAVY_KEYS) {
                    if (Array.isArray(sd[key]) && sd[key].length > 50) delete sd[key];
                }
                return sd;
            });
        }
        return stripped;
    }

    /** localStorage.setItem that clears stale milko_* keys and retries once on QuotaExceededError. */
    private safeSetItem(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
        } catch (e: any) {
            if (e?.name === 'QuotaExceededError' || e?.code === 22) {
                // Evict large non-critical keys and retry once
                const evictable = [this.LOGIN_RAW_KEY, 'milko_dairy_accounts', 'milko_members'];
                evictable.filter(k => k !== key).forEach(k => localStorage.removeItem(k));
                try { localStorage.setItem(key, value); } catch { /* silently fail */ }
            }
        }
    }

    logout(): void {
        localStorage.removeItem(this.USER_KEY);
        localStorage.removeItem(this.LEDGER_CFG_KEY);
        localStorage.removeItem(this.LOGIN_RAW_KEY);
        localStorage.removeItem(this.TOKEN_KEY);
        localStorage.removeItem(this.DAIRY_KEY);
        // Full page reload clears all Angular state (tab cache, body classes,
        // subscription state) and ensures the login page renders cleanly.
        window.location.href = window.location.origin + '/#/auth/login';
    }

    getToken(): string | null {
        return localStorage.getItem(this.TOKEN_KEY);
    }

    /** Replace the stored JWT with a fresh one (e.g. after self-password-change). */
    updateToken(token: string): void {
        localStorage.setItem(this.TOKEN_KEY, token);
    }

    /** Scans all keys in an object for any array whose items have a `voucher_type` field. */
    private findLedgerArray(obj: any): any[] | null {
        if (!obj || typeof obj !== 'object') return null;
        for (const key of Object.keys(obj)) {
            const val = obj[key];
            if (Array.isArray(val) && val.length > 0 && val[0]?.voucher_type !== undefined) {
                return val;
            }
            // One level deep (e.g. res.data.ledgers)
            if (val && typeof val === 'object' && !Array.isArray(val)) {
                for (const innerKey of Object.keys(val)) {
                    const inner = val[innerKey];
                    if (Array.isArray(inner) && inner.length > 0 && inner[0]?.voucher_type !== undefined) {
                        return inner;
                    }
                }
            }
        }
        return null;
    }

    /** Returns the raw ledger-config array from the login response. */
    getLedgerConfig(): any[] {
        // 1. Dedicated cached key (saved on login)
        const cached = localStorage.getItem(this.LEDGER_CFG_KEY);
        if (cached) { const a = JSON.parse(cached); if (a?.length) return a; }

        // 2. Check inside the stored user object (ledgerConfigs may be nested there)
        const user = this.getCurrentUser();
        if (user?.ledgerConfigs?.length)   return user.ledgerConfigs;
        if (user?.ledgerConfig?.length)    return user.ledgerConfig;
        if (user?.ledger_config?.length)   return user.ledger_config;
        if (user?.system_ledgers?.length)  return user.system_ledgers;
        if (user?.voucher_ledgers?.length) return user.voucher_ledgers;
        if (user?.ledgers?.length)         return user.ledgers;

        // 3. Scan the full raw login response for any array with voucher_type items
        const rawLogin = localStorage.getItem(this.LOGIN_RAW_KEY);
        if (rawLogin) {
            const found = this.findLedgerArray(JSON.parse(rawLogin));
            if (found) return found;
        }
        return [];
    }

    /**
     * Returns the system ledger matching voucher_type and optionally ledger_type.
     * @param voucherType e.g. 'PURCHASE'
     * @param ledgerType  e.g. 'Purchase' — optional, used when multiple entries share the same voucher_type
     */
    getSystemLedger(voucherType: string, ledgerType?: string): any | null {
        const list = this.getLedgerConfig();
        if (ledgerType) {
            const exact = list.find(
                (l: any) => (l?.voucher_type || '').toUpperCase() === voucherType.toUpperCase()
                         && (l?.ledger_type  || '').toLowerCase()  === ledgerType.toLowerCase()
            );
            if (exact) return exact;
        }
        return list.find(
            (l: any) => (l?.voucher_type || '').toUpperCase() === voucherType.toUpperCase()
        ) ?? null;
    }

    isLoggedIn(): boolean {
        const user = localStorage.getItem(this.USER_KEY);
        return !!user;
    }

    getCurrentUser(): any {
        try {
            const user = localStorage.getItem(this.USER_KEY);
            const parsed = user ? JSON.parse(user) : null;
            if (Array.isArray(parsed?.dairies) && parsed.dairies.length > 1) {
                const selectedId = Number(localStorage.getItem(this.DAIRY_KEY) || 0);
                const selectedIndex = parsed.dairies.findIndex((dairy: any) => Number(dairy?.id) === selectedId);
                if (selectedIndex > 0) {
                    const [selected] = parsed.dairies.splice(selectedIndex, 1);
                    parsed.dairies.unshift(selected);
                }
            }
            return parsed;
        } catch {
            return null;
        }
    }

    getDairyId(): number {
        return Number(this.getCurrentDairy()?.id || 0);
    }

    getCurrentDairy(): any | null {
        const user = this.getCurrentUser();
        if (!user) return null;
        const dairies = Array.isArray(user.dairies) ? user.dairies : [];
        const selectedId = Number(localStorage.getItem(this.DAIRY_KEY) || 0);
        const selected = dairies.find((dairy: any) => Number(dairy?.id) === selectedId);
        return selected || dairies[0] || user?.dairy || null;
    }

    selectDairy(dairyId: number): boolean {
        const user = this.getCurrentUser();
        const dairy = user?.dairies?.find((item: any) => Number(item?.id) === Number(dairyId));
        if (!dairy) return false;
        localStorage.setItem(this.DAIRY_KEY, String(dairy.id));
        this.userRefreshedSubject.next(user);
        return true;
    }

    private ensureSelectedDairy(user: any): void {
        const dairies = Array.isArray(user?.dairies) ? user.dairies : [];
        const selectedId = Number(localStorage.getItem(this.DAIRY_KEY) || 0);
        if (!dairies.some((dairy: any) => Number(dairy?.id) === selectedId)) {
            if (dairies[0]?.id) localStorage.setItem(this.DAIRY_KEY, String(dairies[0].id));
            else localStorage.removeItem(this.DAIRY_KEY);
        }
    }

    getUserId(): number {
        const user = this.getCurrentUser();
        if (!user) return 0;
        const id =
            user?.mu_id ||
            user?.user_id ||
            user?.userId ||
            user?.u_id ||
            user?.id ||
            0;
        if (id) return Number(id);

        // Fallback: scan the raw login response for any user id field
        try {
            const raw = localStorage.getItem(this.LOGIN_RAW_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                const rawId =
                    parsed?.user?.mu_id || parsed?.user?.user_id || parsed?.user?.id ||
                    parsed?.data?.mu_id || parsed?.data?.user_id || parsed?.data?.id ||
                    parsed?.mu_id || parsed?.user_id || parsed?.id ||
                    0;
                if (rawId) return Number(rawId);
            }
        } catch { /* ignore */ }
        return 0;
    }

    getUserName(): string {
        const user = this.getCurrentUser();
        if (!user) return '';
        return user?.name || user?.user_name || user?.username || user?.full_name || '';
    }

    /**
     * Re-fetches the current user's data from the server and updates localStorage.
     * Called periodically by the shell so permission/status changes take effect
     * without requiring a manual re-login.
     * Errors are silently ignored — if the endpoint does not exist yet or
     * the server rejects the JWT, the interceptor handles forced logout.
     */
    refreshCurrentUser(): Observable<any | null> {
        const userId = this.getUserId();
        const dairyId = this.getDairyId();
        if (!userId) return of(null);
        return this.api.post<any>('users/validate_session.php', { u_id: userId, d_id: dairyId }).pipe(
            map((res: any) => {
                if (res?.success && (res?.user || res?.data)) {
                    const fresh = res.user || res.data;
                    const cachedUser = this.stripHeavyFields(fresh);
                    this.safeSetItem(this.USER_KEY, JSON.stringify(cachedUser));
                    this.ensureSelectedDairy(cachedUser);
                    if (res?.token) {
                        localStorage.setItem(this.TOKEN_KEY, res.token);
                    }
                    const cfg = res?.ledgerConfigs ?? res?.ledgerConfig ?? res?.ledger_config
                              ?? res?.system_ledgers ?? res?.voucher_ledgers ?? res?.ledgers ?? null;
                    if (cfg) {
                        this.safeSetItem(this.LEDGER_CFG_KEY, JSON.stringify(cfg));
                    }
                    this.userRefreshedSubject.next(cachedUser);
                    return cachedUser;
                }
                return null;
            }),
            catchError((err: any) => {
                // If the server explicitly signals session expired (401 + jwt_error),
                // the interceptor has already navigated to login. For all other errors
                // (500, network, etc.) silently ignore so the app stays usable.
                if (err?.status === 401 || err?.errorBody?.jwt_error) {
                    localStorage.removeItem(this.TOKEN_KEY);
                    localStorage.removeItem(this.USER_KEY);
                }
                return of(null);
            })
        );
    }
}
